import { 
  getInviteByCode as getInviteFirestore, 
  createInvite, 
  updateInvite, 
  deleteInvite as deleteInviteFirestore, 
  getCourseInvites as getCourseInvitesFirestore 
} from '../firebase/unifiedDataService';
import { Logger } from './logger';

const log = Logger.create('courseInviteService');

/**
 * Course Invitation Service
 * Handles creating, validating, and managing course invite codes
 * 
 * Uses unifiedDataService for consistent data access
 */

/**
 * Generate a random invite code
 * @param {number} length - Length of the code (default: 6)
 * @returns {string} - Random alphanumeric code
 */
function generateInviteCode(length = 6) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars like O, 0, I, 1
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a new course invitation in Firestore
 * @param {Object} params - Invitation parameters
 * @param {string} params.teacherUserId - Teacher's user ID
 * @param {string} params.courseId - Course ID
 * @param {string} params.courseName - Course name (for display)
 * @param {number} [params.expiresInDays] - Days until expiration (optional)
 * @param {number} [params.usageLimit] - Max uses (optional, 0 = unlimited)
 * @param {string} [params.label] - Custom label for the invite
 * @returns {Promise<Object>} - Created invite object with code
 */
export async function createCourseInvite({
  teacherUserId,
  courseId,
  courseName,
  expiresInDays = null,
  usageLimit = 0,
  label = ''
}) {
  if (!teacherUserId || !courseId) {
    throw new Error('teacherUserId und courseId sind erforderlich');
  }
  
  // Generate unique code (try up to 5 times)
  let code = null;
  let attempts = 0;
  
  while (!code && attempts < 5) {
    const candidate = generateInviteCode();
    const existing = await getInviteFirestore(candidate);
    
    if (!existing) {
      code = candidate;
    }
    attempts++;
  }
  
  if (!code) {
    throw new Error('Konnte keinen eindeutigen Code generieren. Bitte versuche es erneut.');
  }
  
  // Calculate expiration
  let expiresAt = null;
  if (expiresInDays && expiresInDays > 0) {
    expiresAt = Date.now() + (expiresInDays * 24 * 60 * 60 * 1000);
  }
  
  // Create invite object
  const invite = {
    code,
    teacherUserId,
    courseId,
    courseName,
    label: label || `Einladung für ${courseName}`,
    isActive: true,
    expiresAt,
    usageLimit: usageLimit || null,
    usageCount: 0
  };
  
  // Save to Firestore using code as document ID
  await createInvite(code, invite);
  
  return { ...invite, code };
}

/**
 * Get invite by code from Firestore
 * @param {string} code - Invite code
 * @returns {Promise<Object|null>} - Invite object or null if not found
 */
export async function getInviteByCode(code) {
  if (!code) return null;
  
  return await getInviteFirestore(code.toUpperCase());
}

/**
 * Validate an invite code
 * @param {string} code - Invite code to validate
 * @returns {Promise<Object>} - { valid: boolean, error?: string, invite?: Object }
 */
export async function validateInviteCode(code) {
  const invite = await getInviteByCode(code);
  
  if (!invite) {
    return { valid: false, error: 'Einladungscode nicht gefunden.' };
  }
  
  if (!invite.isActive) {
    return { valid: false, error: 'Dieser Einladungscode wurde deaktiviert.' };
  }
  
  if (invite.expiresAt && Date.now() > invite.expiresAt) {
    return { valid: false, error: 'Dieser Einladungscode ist abgelaufen.' };
  }
  
  if (invite.usageLimit && invite.usageCount >= invite.usageLimit) {
    return { valid: false, error: 'Dieser Einladungscode hat das Nutzungslimit erreicht.' };
  }
  
  return { valid: true, invite };
}

/**
 * Deactivate an invite in Firestore
 * @param {string} code - Invite code
 * @param {string} teacherUserId - Teacher's user ID (for verification)
 * @returns {Promise<boolean>} - Success
 */
export async function deactivateInvite(code, teacherUserId) {
  const invite = await getInviteByCode(code);
  
  if (!invite) {
    throw new Error('Einladungscode nicht gefunden.');
  }
  
  if (invite.teacherUserId !== teacherUserId) {
    throw new Error('Keine Berechtigung, diesen Einladungscode zu deaktivieren.');
  }
  
  await updateInvite(code.toUpperCase(), { isActive: false });
  
  return true;
}

/**
 * Delete an invite from Firestore
 * @param {string} code - Invite code
 * @param {string} teacherUserId - Teacher's user ID (for verification)
 * @returns {Promise<boolean>} - Success
 */
export async function deleteInvite(code, teacherUserId) {
  const invite = await getInviteByCode(code);
  
  if (!invite) {
    throw new Error('Einladungscode nicht gefunden.');
  }
  
  if (invite.teacherUserId !== teacherUserId) {
    throw new Error('Keine Berechtigung, diesen Einladungscode zu löschen.');
  }
  
  await deleteInviteFirestore(code.toUpperCase());
  
  return true;
}

/**
 * Get all invites for a course from Firestore
 * @param {string} teacherUserId - Teacher's user ID (not used, kept for API compatibility)
 * @param {string} courseId - Course ID
 * @returns {Promise<Array>} - Array of invites
 */
export async function getCourseInvites(teacherUserId, courseId) {
  const invites = await getCourseInvitesFirestore(courseId);
  
  return invites.sort((a, b) => {
    // Sort by creation date, newest first
    const aTime = a.createdAt || 0;
    const bTime = b.createdAt || 0;
    return bTime - aTime;
  });
}
/**
 * Generate invite link
 * @param {string} code - Invite code
 * @param {string} [baseUrl] - Base URL (defaults to current origin)
 * @returns {string} - Full invite URL
 */
export function getInviteLink(code, baseUrl = null) {
  const base = baseUrl || window.location.origin;
  return `${base}/join/${code.toUpperCase()}`;
}

/**
 * Copy invite link to clipboard
 * @param {string} code - Invite code
 * @returns {Promise<boolean>} - Success
 */
export async function copyInviteLinkToClipboard(code) {
  const link = getInviteLink(code);
  
  try {
    await navigator.clipboard.writeText(link);
    return true;
  } catch (err) {
    log.error('Failed to copy to clipboard:', err);
    // Fallback
    const textArea = document.createElement('textarea');
    textArea.value = link;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
}

export default {
  createCourseInvite,
  getInviteByCode,
  validateInviteCode,
  deactivateInvite,
  deleteInvite,
  getCourseInvites,
  getInviteLink,
  copyInviteLinkToClipboard
};
